import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragSmall } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box } from '@mui/material';

interface DragHandleProps {
  rounded: Record<string, any>;
}

/**
 * Hover-revealed drag handle. Absolutely positioned (and pointer-events:none)
 * so it never contributes to its parent's measured rect — important for
 * dnd-kit's collision detection, which caches each item's bounding rect.
 */
const DragHandle = ({ rounded }: DragHandleProps) => (
  <Box
    sx={{
      opacity: 0,
      ...flex.center,
      ...rounded,
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      pointerEvents: 'none',
      zIndex: theme => theme.zIndex.modal,
      'div:hover > &, &:active': {
        opacity: 1,
        '+ span .MuiButton-filterPrimary > span:first-of-type': {
          maskImage: 'linear-gradient(to right, transparent 7px, black 40px, black)',
        },
      },
    }}>
    <DragSmall />
  </Box>
);

interface SortableFilterProps {
  id: string;
  disabled: boolean;
  showHandle: boolean;
  rounded: Record<string, any>;
  children: React.ReactNode;
}

/**
 * One sortable filter chip. Owns its own `useSortable` instance and renders a
 * positioned wrapper whose rect dnd-kit can measure.
 */
export const SortableFilter = React.memo(({ id, disabled, showHandle, rounded, children }: SortableFilterProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isSorting } = useSortable({
    id,
    disabled,
    transition: { duration: 80, easing: 'ease-out' },
  });

  // useSortable's `attributes` (role, tabIndex, aria-disabled, aria-roledescription,
  // aria-describedby) collide with the inner filter button: e2e specs across the app
  // use getByRole('button', { name: '...' }) and Playwright's actionability check walks
  // ancestor aria-disabled. The inner button already exposes the correct role and
  // disabled state, so we only forward the keyboard-affordance bits and only while the
  // sortable is enabled (i.e. on draggable filter bars).
  const keyboardAttrs = disabled
    ? {}
    : {
        tabIndex: attributes.tabIndex,
        'aria-describedby': attributes['aria-describedby'],
        'aria-roledescription': attributes['aria-roledescription'],
      };

  return (
    <Box
      ref={setNodeRef}
      data-testid={id}
      sx={{
        position: 'relative',
        transform: CSS.Translate.toString(transform),
        transition: isSorting ? transition : undefined,
        opacity: isDragging ? 0 : 1,
      }}
      {...keyboardAttrs}
      {...listeners}>
      {showHandle && <DragHandle rounded={rounded} />}
      {children}
    </Box>
  );
});
